/**
 * ค่า configuration ของระบบ
 *
 * หลักการ: ทุกค่าที่ผู้ว่าจ้างยังไม่ยืนยัน ต้องอยู่ในไฟล์นี้เป็น "ค่าเริ่มต้นที่ปลอดภัย"
 * และผู้ดูแลระบบแก้ไขได้ผ่านหน้า Settings (เก็บลงตาราง app_settings)
 * ห้าม hard-code ค่าเหล่านี้กระจายอยู่ในโค้ดส่วนอื่น
 */

export interface RegistrationSettings {
  /**
   * regex ที่ใช้ตรวจเลขทะเบียน "หลัง normalize แล้ว"
   * null = ไม่ตรวจรูปแบบเพิ่ม ตรวจเฉพาะความยาวและชุดอักขระ
   *
   * !! ผู้ว่าจ้างยังไม่ยืนยันรูปแบบจริงและ check digit — ค่าเริ่มต้นจึงกว้างไว้ก่อน
   *    เมื่อทราบรูปแบบแล้วให้ตั้งค่านี้ผ่านหน้า Settings ไม่ต้องแก้โค้ด
   */
  pattern: string | null;
  minLength: number;
  maxLength: number;
  /** แปลงเป็นตัวพิมพ์ใหญ่ตอน normalize */
  uppercase: boolean;
  /** อักขระที่ตัดทิ้งตอน normalize (เช่น ขีด เว้นวรรค) — เป็น regex character class */
  stripPattern: string;
  /** อนุญาตให้เลขทะเบียนเดียวมี active record ได้มากกว่าหนึ่งรายการหรือไม่ */
  allowDuplicateActive: boolean;
  /** ถ้า allowDuplicateActive = true บทบาทขั้นต่ำที่กดยืนยันบันทึกซ้ำได้ */
  duplicateOverrideMinRole: 'field_officer' | 'supervisor' | 'administrator';
}

export interface GpsSettings {
  /** เกณฑ์ความแม่นยำสูงสุดที่ยอมรับได้ (เมตร) */
  thresholdM: number;
  /** ตัวเลือกที่ผู้ดูแลระบบเลือกได้ในหน้า Settings */
  thresholdOptionsM: number[];
  /** เวลารอสูงสุดระหว่าง watchPosition (วินาที) */
  maxWaitSeconds: number;
  /** พิกัดเก่าเกินกี่วินาทีถือว่าใช้ไม่ได้ ต้องวัดใหม่ */
  maxPositionAgeSeconds: number;
  /** เกณฑ์แบ่งระดับคุณภาพสำหรับแสดงผล (เมตร, เรียงจากดีที่สุด) */
  qualityExcellentM: number;
  qualityGoodM: number;
  qualityFairM: number;
  /** อนุญาตให้ขออนุมัติ override เมื่อ GPS ไม่ผ่านเกณฑ์หรือไม่ */
  allowSupervisorOverride: boolean;
  /** ความยาวขั้นต่ำของเหตุผลที่ขอ override */
  overrideReasonMinLength: number;
}

export interface ImageSettings {
  /** ด้านยาวสูงสุดหลังย่อฝั่ง client (px) */
  maxLongEdgePx: number;
  /** คุณภาพเริ่มต้นตอนบีบอัด (0-1) — ตัวบีบอัดจะลดลงเองถ้ายังเกิน targetMaxBytes */
  quality: number;
  /** ขนาดไฟล์สูงสุดหลังประมวลผล (ไบต์) — server ปฏิเสธถ้าเกิน */
  maxBytes: number;
  /**
   * ขนาดเป้าหมายที่ตัวบีบอัดฝั่ง client ต้องทำให้ได้
   *
   * ไม่ใช่แค่คำแนะนำ: ตัวบีบอัดจะวนลดคุณภาพและย่อขนาดจนกว่าไฟล์จะไม่เกิน
   * targetMaxBytes เพื่อให้งบพื้นที่จัดเก็บคาดการณ์ได้จริง ไม่ว่าภาพจะซับซ้อนแค่ไหน
   * (ตั้ง quality อย่างเดียวไม่พอ เพราะภาพที่มีรายละเอียดมากจะได้ไฟล์ใหญ่กว่ามาก)
   */
  targetMinBytes: number;
  targetMaxBytes: number;
  /** ชนิดไฟล์ที่รับจากกล้อง/ไฟล์ */
  acceptedInputTypes: string[];
  /** ชนิดไฟล์ที่เก็บจริงหลังแปลง */
  outputContentType: 'image/jpeg' | 'image/webp';
  /** จำนวนรูปต่อหนึ่งรายการ — ผู้ว่าจ้างยังไม่ยืนยัน ค่าเริ่มต้น 1 */
  maxPerCapture: number;
  /** อายุของ signed URL สำหรับเปิดดูรูป (วินาที) */
  viewUrlTtlSeconds: number;
  /** อายุของสิทธิ์อัปโหลด (วินาที) */
  uploadUrlTtlSeconds: number;
}

export interface CaptureSettings {
  /** เจ้าหน้าที่ภาคสนามแก้ไขรายการของตนได้ภายในกี่นาทีหลังบันทึกสำเร็จ */
  ownerEditWindowMinutes: number;
  /** draft ที่ไม่ finalize ภายในกี่นาที ให้ cleanup job ลบทิ้งพร้อม orphan object */
  draftTtlMinutes: number;
}

export interface ExportSettings {
  /** จำนวนแถวสูงสุดต่อหนึ่งงาน export */
  maxRows: number;
  /** จำนวนแถวที่ประมวลผลต่อหนึ่งรอบของ background job */
  chunkRows: number;
  /** ไฟล์ export หมดอายุใน R2 หลังกี่ชั่วโมง */
  ttlHours: number;
}

export interface RetentionSettings {
  /**
   * อายุการเก็บข้อมูล — ผู้ว่าจ้างต้องยืนยันตามนโยบาย/กฎหมายของหน่วยงาน
   * 0 = ไม่ลบอัตโนมัติ (ค่าเริ่มต้นที่ปลอดภัยที่สุด ป้องกันข้อมูลหายโดยไม่ตั้งใจ)
   */
  captureDays: number;
  imageDays: number;
  auditLogDays: number;
}

export interface UserSettings {
  /**
   * จำนวนบัญชีที่ active ได้สูงสุด — ระบบจะปฏิเสธการเพิ่มผู้ใช้เกินจำนวนนี้
   *
   * ที่ต้องมีเพดานนี้เพราะเป็นเรื่องค่าใช้จ่าย ไม่ใช่เรื่องเทคนิค:
   * Cloudflare Zero Trust ให้ใช้ฟรีถึง 50 บัญชี ถ้าเกินแม้แต่คนเดียว
   * ค่าใช้จ่ายจะเปลี่ยนจากประมาณ $6 เป็นประมาณ $7 ต่อคนต่อเดือนทั้งหมด
   * (40 คน = ฟรี, 51 คน = ประมาณ $357/เดือน)
   *
   * ผู้ว่าจ้างกำหนดไว้ที่ 40 คน ค่านี้จึงเป็น 40
   * ถ้าจะเพิ่มเกิน 50 ต้องยืนยันงบประมาณกับผู้ว่าจ้างก่อนเสมอ
   */
  maxActiveUsers: number;
  /** เริ่มเตือนผู้ดูแลระบบเมื่อจำนวนบัญชี active ถึงค่านี้ */
  seatWarningThreshold: number;
  /**
   * เพดานของแพลตฟอร์มที่ข้ามแล้วมีค่าใช้จ่าย ใช้แสดงคำเตือนเท่านั้น
   * ไม่ได้บังคับ เพราะผู้ว่าจ้างอาจตัดสินใจจ่ายเพิ่มในอนาคต
   */
  freeSeatLimit: number;
}

export interface AuthSettings {
  /**
   * อายุ session เป็นวัน
   *
   * ผู้ว่าจ้างเลือก 30 วัน เพื่อไม่ให้เจ้าหน้าที่ต้องล็อกอินบ่อยระหว่างทำงานภาคสนาม
   * ข้อแลกเปลี่ยนที่รับทราบแล้ว: ถ้ามือถือหาย ผู้ที่เก็บได้ใช้งานต่อได้จนกว่าจะหมดอายุ
   * จึงต้องมีปุ่มเพิกถอน session ให้ผู้ดูแลกดได้ทันที และการระงับบัญชีตัด session ทันที
   */
  sessionDays: number;
  /** ใส่รหัสผิดติดกันกี่ครั้งแล้วล็อกบัญชีชั่วคราว */
  maxFailedAttempts: number;
  /** ล็อกนานกี่นาที */
  lockoutMinutes: number;
  /** ความยาวรหัสผ่านขั้นต่ำ */
  minPasswordLength: number;
  /**
   * จำนวนรอบของ PBKDF2
   *
   * !! Cloudflare Workers จำกัดไว้ที่ 100,000 รอบ ตั้งเกินกว่านี้ login จะพังทั้งระบบ
   *    ด้วย NotSupportedError ไม่ใช่ข้อจำกัดที่เราเลือกเอง แต่เป็นเพดานของแพลตฟอร์ม
   *
   * ค่านี้ต่ำกว่าที่ OWASP แนะนำ (600,000)
   * ผู้ว่าจ้างเลือกความยาวรหัสขั้นต่ำ 8 ตัวเพื่อให้เจ้าหน้าที่จำและพิมพ์บนมือถือได้ง่าย
   * การเดาผ่านหน้าเว็บยังถูกกันด้วยการล็อกบัญชีหลังผิด 8 ครั้ง
   * ส่วนที่อ่อนลงคือกรณีฐานข้อมูลรั่วแล้วนำ hash ไปไล่เดาแบบออฟไลน์
   */
  pbkdf2Iterations: number;
}

export interface AppSettings {
  registration: RegistrationSettings;
  gps: GpsSettings;
  image: ImageSettings;
  capture: CaptureSettings;
  export: ExportSettings;
  retention: RetentionSettings;
  users: UserSettings;
  auth: AuthSettings;
}

/** คีย์ในตาราง app_settings — หนึ่งคีย์ต่อหนึ่งกลุ่ม เก็บเป็น JSON */
export const SETTINGS_KEYS = [
  'registration',
  'gps',
  'image',
  'capture',
  'export',
  'retention',
  'users',
  'auth',
] as const;
export type SettingsKey = (typeof SETTINGS_KEYS)[number];

export const DEFAULT_SETTINGS: AppSettings = {
  registration: {
    pattern: null,
    minLength: 4,
    maxLength: 32,
    uppercase: true,
    stripPattern: '[\\s\\-_.()/]',
    allowDuplicateActive: false,
    duplicateOverrideMinRole: 'supervisor',
  },
  gps: {
    thresholdM: 20,
    thresholdOptionsM: [10, 20, 30, 50],
    maxWaitSeconds: 60,
    maxPositionAgeSeconds: 60,
    qualityExcellentM: 10,
    qualityGoodM: 20,
    qualityFairM: 35,
    allowSupervisorOverride: true,
    overrideReasonMinLength: 10,
  },
  /**
   * วัตถุประสงค์ของรูป (ผู้ว่าจ้างยืนยัน 2026-08-23)
   *   เก็บเป็นหลักฐานว่าเจ้าหน้าที่ไปถึงจุดนั้นจริง
   *   ไม่มีการอ่านเลขจากภาพ ไม่มี OCR ปกติไม่มีใครเปิดดูอีกเลยยกเว้นมีข้อพิพาท
   *
   * ค่าจึงตั้งให้เล็กที่สุดเท่าที่ยังเห็นได้ว่าเป็นมิเตอร์และเป็นสถานที่ไหน
   *
   *   งบพื้นที่ : 200,000 x 45 KB = ประมาณ 9.0 GB อยู่ในโควตาฟรีของ R2
   *   อัปโหลด   : ประมาณ 0.7 วินาทีต่อรูปที่สัญญาณอ่อน 0.5 Mbps
   *
   * เหตุผลหลักคือเวลาอัปโหลด ไม่ใช่ค่าใช้จ่าย
   * ค่า R2 ต่างกันแค่เดือนละประมาณ 21 บาท ซึ่งไม่มีนัยสำคัญ
   * แต่เวลาอัปโหลดต่างกัน 5 เท่า คูณด้วย 200,000 ครั้งที่เจ้าหน้าที่ต้องยืนรอหน้างาน
   * และยิ่งอัปโหลดสั้น โอกาสสายหลุดกลางคันยิ่งน้อย
   *
   * เพดานแข็ง 90 KB เป็นสองเท่าพอดีของเป้าหมาย กรณีเลวร้ายสุดไม่เกิน 18 GB
   *
   * ถ้าภายหลังพบว่าต้องอ่านเลขจากภาพจริง ให้กลับไปที่ 1600 px / 250 KB
   * ปรับได้จากหน้าตั้งค่าโดยไม่ต้อง deploy แต่มีผลเฉพาะรูปที่ถ่ายหลังจากนั้น
   */
  image: {
    maxLongEdgePx: 1024,
    quality: 0.72,
    maxBytes: 90 * 1024,
    targetMinBytes: 25 * 1024,
    targetMaxBytes: 45 * 1024,
    acceptedInputTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
    outputContentType: 'image/jpeg',
    maxPerCapture: 1,
    viewUrlTtlSeconds: 300,
    uploadUrlTtlSeconds: 600,
  },
  capture: {
    ownerEditWindowMinutes: 120,
    draftTtlMinutes: 180,
  },
  export: {
    maxRows: 200_000,
    chunkRows: 5_000,
    ttlHours: 24,
  },
  retention: {
    captureDays: 0,
    imageDays: 0,
    auditLogDays: 0,
  },
  users: {
    maxActiveUsers: 40,
    seatWarningThreshold: 35,
    freeSeatLimit: 50,
  },
  auth: {
    sessionDays: 30,
    maxFailedAttempts: 8,
    lockoutMinutes: 15,
    minPasswordLength: 8,
    pbkdf2Iterations: 100_000,
  },
};

/** ชนิดไฟล์ที่ยอมให้เก็บใน R2 จริง ๆ (ตรวจจาก magic bytes ไม่ใช่จาก Content-Type ของ client) */
export const STORABLE_CONTENT_TYPES = ['image/jpeg', 'image/webp', 'image/png'] as const;
export type StorableContentType = (typeof STORABLE_CONTENT_TYPES)[number];

/** เวลาแสดงผลทั้งระบบ — เก็บ UTC ในฐานข้อมูล แสดงผลด้วยโซนนี้ */
export const DISPLAY_TIME_ZONE = 'Asia/Bangkok';

/** ขนาดหน้าเริ่มต้น/สูงสุดของ list API — กันไม่ให้ดึง 200,000 แถวลง browser */
export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 100;

export function mergeSettings(partial: Partial<AppSettings> | null | undefined): AppSettings {
  if (!partial) return structuredClone(DEFAULT_SETTINGS);
  const base = structuredClone(DEFAULT_SETTINGS);
  for (const key of SETTINGS_KEYS) {
    const group = partial[key];
    if (group && typeof group === 'object') {
      Object.assign(base[key], group);
    }
  }
  return base;
}
